import {useEffect, useRef, useState} from "react";
import {TorresstreamApi} from "../../../../apis/torrestream-api/torresstream-api";
import {AIOStreamAPI} from "../../../../apis/aiostream-api/aiostream-api";
import {fetchEpisodeGroupDetails} from "../../api/cap17drive-api";
import {getEmbedUrl} from "../../../../utils/legalServers";
import {TORRESOWO_URL} from "../../../../utils/consts";
import {
    fetchGetTorresowo,
    getAudioFromMetadata,
    getDurationFromMetadata,
    getSubtitlesFromMetadata
} from "../../../../utils/utils";
import auth from "../../../../utils/authentication";

export type StreamOption = {
    id: string;
    title: string;
    subtitle?: string;
    badge?: string;
    raw: any;
};

export type TvStreamState = {
    loading: boolean;
    error: string | null;
    videoUrl: string | null;
    originalVideoUrl: string | null;
    iframeSrc: string | null;
    duration: number; // in seconds
    durationMinutes: number; // in minutes
    subtitles: any[];
    audios: any[];
    streams: StreamOption[];
    selectedStream: StreamOption | null;
    selectStream: (stream: StreamOption) => void;
    downloadInfo: any | null;
    isStreaming: boolean;
};

export const useTvStreamResolver = (
    selectedServer: string | null,
    item: any,
    file: any,
    type: "movie" | "tv",
    tmdbId: string | number,
    season?: number | string,
    episode?: number | string
): TvStreamState => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [originalVideoUrl, setOriginalVideoUrl] = useState<string | null>(null);
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);
    const [duration, setDuration] = useState<number>(0); // in minutes
    const [subtitles, setSubtitles] = useState<any[]>([]);
    const [audios, setAudios] = useState<any[]>([]);
    const [streams, setStreams] = useState<StreamOption[]>([]);
    const [selectedStream, setSelectedStream] = useState<StreamOption | null>(null);
    const [downloadInfo, setDownloadInfo] = useState<any | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);

    const downloadIntervalRef = useRef<any>(null);
    const lastServerRef = useRef<string | null>(null);

    // Initial fallback runtime from TMDB item (in minutes)
    const initialRuntimeMins =
        item?.runtime ||
        item?.episode?.runtime ||
        item?.serie?.episode_run_time?.[0] ||
        0;

    // Reset when server changes or media item changes
    useEffect(() => {
        if (lastServerRef.current !== selectedServer) {
            lastServerRef.current = selectedServer;
            setSelectedStream(null);
            setStreams([]);
            setVideoUrl(null);
            setOriginalVideoUrl(null);
            setIframeSrc(null);
            setSubtitles([]);
            setAudios([]);
            setDownloadInfo(null);
            setError(null);
            setDuration(initialRuntimeMins);
        }
    }, [selectedServer, tmdbId, season, episode, initialRuntimeMins]);

    // Clean up torrent download progress polling
    useEffect(() => {
        return () => {
            if (downloadIntervalRef.current) {
                clearInterval(downloadIntervalRef.current);
                downloadIntervalRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!selectedServer || !item) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);
        setError(null);

        // 1. SquadFlix Server (direct SquadDisk file)
        if (selectedServer === "SquadFlix" && file) {
            setIsStreaming(true);
            setIframeSrc(null);
            setStreams([]);
            const url = TorresstreamApi.get("streamSquadDiskFile").getUrl({filePath: file.url});
            setVideoUrl(url);
            const origUrl = type === "tv"
                ? `${TORRESOWO_URL}/squadflix/${item?.serie?.id || tmdbId}_${item?.season_number || season}_${item?.episode_number || episode}.mp4`
                : `${TORRESOWO_URL}/squadflix/${item?.id || tmdbId}.mp4`;
            setOriginalVideoUrl(origUrl);
            setLoading(false);
            if (initialRuntimeMins > 0) setDuration(initialRuntimeMins);

            TorresstreamApi.get("streamSquadDiskFile")
                .call({filePath: file.url, fetchMetadata: true})
                .then((res: any) => {
                    if (!isMounted) return;
                    const dur = getDurationFromMetadata(res.metadata, item);
                    if (dur) setDuration(dur);
                    setSubtitles(getSubtitlesFromMetadata(res.metadata));
                    setAudios(getAudioFromMetadata(res.metadata));
                })
                .catch(() => {});
            return;
        }

        // 2. AIOStream Server
        if (selectedServer === "AIOStream") {
            setIsStreaming(true);
            setIframeSrc(null);
            if (initialRuntimeMins > 0) setDuration(initialRuntimeMins);

            const imdbId =
                item?.imdb_id ||
                item?.external_ids?.imdb_id ||
                item?.serie?.external_ids?.imdb_id ||
                item?.serie?.imdb_id;

            const fetchStreams = async () => {
                try {
                    let targetImdbId = imdbId;
                    if (!targetImdbId) {
                        if (type === "tv") {
                            const serieData = await fetchGetTorresowo(`/tmdb/serie/${tmdbId}?uid=${auth.currentUser?.uid}`).catch(() => null);
                            targetImdbId = serieData?.external_ids?.imdb_id;
                        } else {
                            const movieData = await fetchGetTorresowo(`/tmdb/movie/${tmdbId}?uid=${auth.currentUser?.uid}`).catch(() => null);
                            targetImdbId = movieData?.external_ids?.imdb_id || movieData?.imdb_id;
                        }
                    }

                    if (!targetImdbId) {
                        setError("No se encontró identificador IMDb para consultar AIOStream.");
                        setLoading(false);
                        return;
                    }

                    let streamList: any[] = [];
                    if (type === "tv" && season != null && episode != null) {
                        const epDetails = await fetchEpisodeGroupDetails(tmdbId, season, episode).catch(() => null);
                        const sNum = epDetails?.season_number ?? season;
                        const eNum = epDetails?.episode_number ?? episode;
                        const data = await AIOStreamAPI.getSerieStreams(
                            targetImdbId,
                            sNum,
                            eNum
                        );
                        streamList = data?.streams || [];
                    } else {
                        const data = await AIOStreamAPI.getMovieStreams(targetImdbId);
                        streamList = data?.streams || [];
                    }

                    if (!isMounted) return;

                    const formattedOptions: StreamOption[] = streamList.map((st: any, idx: number) => ({
                        id: st.url || `stream_${idx}`,
                        title: st.name || `Stream ${idx + 1}`,
                        subtitle: st.description?.replace(/<[^>]*>?/gm, " ").trim(),
                        badge: st.name?.includes("⚡") ? "Rápido" : undefined,
                        raw: st
                    }));

                    setStreams(formattedOptions);

                    // Auto-select fast/cached stream or first
                    const streamToUse =
                        formattedOptions.find(o => o.title.includes("⚡") || !o.title.includes("⏳")) ||
                        formattedOptions[0];

                    if (streamToUse) {
                        setSelectedStream(streamToUse);
                        const streamUrl = streamToUse.raw.url;
                        const vUrl = TorresstreamApi.get("streamRemoteFile").getUrl({fileUrl: streamUrl});
                        setVideoUrl(vUrl);
                        setOriginalVideoUrl(streamUrl);
                        setLoading(false); // Signal video URL ready immediately!

                        TorresstreamApi.getFileUrlMetadata(streamUrl)
                            .then((metaRes: any) => {
                                if (!isMounted) return;
                                const dur = getDurationFromMetadata(metaRes.metadata, item);
                                if (dur) setDuration(dur);
                                setAudios(getAudioFromMetadata(metaRes.metadata));
                                setSubtitles(getSubtitlesFromMetadata(metaRes.metadata));
                            })
                            .catch(() => {});
                    } else {
                        setError("No se encontraron streams disponibles en AIOStream.");
                        setLoading(false);
                    }
                } catch (err: any) {
                    if (isMounted) {
                        setError("Error al cargar streams de AIOStream.");
                        setLoading(false);
                    }
                }
            };

            fetchStreams();
            return;
        }

        // 3. TowersStream Server
        if (selectedServer === "TowersStream") {
            setIsStreaming(true);
            setIframeSrc(null);
            if (initialRuntimeMins > 0) setDuration(initialRuntimeMins);

            const loadTorrents = async () => {
                try {
                    let torrentData: any;
                    if (type === "movie") {
                        torrentData = await TorresstreamApi.getMovieTorrents(tmdbId, false);
                    } else {
                        torrentData = await TorresstreamApi.getEpisodeTorrents(tmdbId, season, episode, false);
                    }

                    if (!isMounted) return;

                    const allTorrents = [
                        ...(torrentData?.existingTorrents || []),
                        ...(torrentData?.torrents || [])
                    ];

                    const formattedOptions: StreamOption[] = allTorrents.map((tor: any) => ({
                        id: tor.id || tor.infoHash,
                        title: tor.name || `Torrent ${tor.id}`,
                        subtitle: tor.downloaded_size
                            ? `${(tor.downloaded_size / (1024 * 1024)).toFixed(1)} MB descargados`
                            : undefined,
                        badge: tor.existing ? "Descargado" : undefined,
                        raw: tor
                    }));

                    setStreams(formattedOptions);

                    const torrentToUse =
                        formattedOptions.find(o => o.raw.existing) ||
                        formattedOptions[0];

                    if (torrentToUse) {
                        setSelectedStream(torrentToUse);
                        const torId = torrentToUse.raw.id;

                        TorresstreamApi.downloadTorrent(torId, tmdbId, season, episode).catch(() => {});

                        const vUrl = getEmbedUrl("TowersStream", undefined, torId, undefined, undefined);
                        setVideoUrl(vUrl);
                        setOriginalVideoUrl(TorresstreamApi.get("originalFile").getUrl({torrentId: torId}));
                        setLoading(false); // Video URL ready immediately!

                        TorresstreamApi.getTorrentFileDetails(torId)
                            .then((metaRes: any) => {
                                if (!isMounted) return;
                                const dur = getDurationFromMetadata(metaRes.metadata, item);
                                if (dur) setDuration(dur);
                                setAudios(getAudioFromMetadata(metaRes.metadata));
                                setSubtitles(getSubtitlesFromMetadata(metaRes.metadata));
                            })
                            .catch(() => {});

                        // Poll download progress
                        if (downloadIntervalRef.current) clearInterval(downloadIntervalRef.current);
                        downloadIntervalRef.current = setInterval(() => {
                            TorresstreamApi.getDownloadProgress(torId)
                                .then((prog: any) => {
                                    if (isMounted) setDownloadInfo(prog);
                                    if (!prog.downloading && !prog.converting) {
                                        if (downloadIntervalRef.current) {
                                            clearInterval(downloadIntervalRef.current);
                                            downloadIntervalRef.current = null;
                                        }
                                    }
                                })
                                .catch(() => {});
                        }, 1200);
                    } else {
                        setError("No se encontraron torrents para este contenido.");
                        setLoading(false);
                    }
                } catch {
                    if (isMounted) {
                        setError("Error al cargar torrents de TowersStream.");
                        setLoading(false);
                    }
                }
            };

            loadTorrents();
            return;
        }

        // 4. VerOnline Server
        if (selectedServer === "VerOnline") {
            const fetchVeronline = async () => {
                try {
                    let links: any[] = [];
                    if (type === "movie") {
                        links = await TorresstreamApi.fetchOnlineMovie(tmdbId);
                    } else {
                        links = await TorresstreamApi.fetchOnlineSerieEpisode(tmdbId, season, episode);
                    }

                    if (!isMounted) return;

                    const sorted = (links || []).sort((a: any, b: any) => {
                        const aEs = a.language === "es" ? 1 : 0;
                        const bEs = b.language === "es" ? 1 : 0;
                        return bEs - aEs || (b.customFunction ? 1 : 0) - (a.customFunction ? 1 : 0);
                    });

                    const formattedOptions: StreamOption[] = sorted.map((lnk: any, idx: number) => ({
                        id: lnk.link || `link_${idx}`,
                        title: `${lnk.service} (${lnk.language || "es"})`,
                        badge: lnk.customFunction ? "Nativo" : "Web",
                        raw: lnk
                    }));

                    setStreams(formattedOptions);

                    const linkToUse = formattedOptions[0];

                    if (linkToUse) {
                        setSelectedStream(linkToUse);
                        if (linkToUse.raw.customFunction) {
                            setIsStreaming(true);
                            setIframeSrc(null);
                            const url = TorresstreamApi.get(linkToUse.raw.customFunction).getUrl({
                                link: linkToUse.raw.link,
                                tmdbId,
                                season,
                                episode
                            });
                            setVideoUrl(url);
                            setOriginalVideoUrl(url);
                        } else {
                            setIsStreaming(false);
                            setVideoUrl(null);
                            setIframeSrc(linkToUse.raw.link);
                        }
                    } else {
                        setError("No se encontraron enlaces en VerOnline.");
                    }
                    setLoading(false);
                } catch {
                    if (isMounted) {
                        setError("Error al cargar enlaces de VerOnline.");
                        setLoading(false);
                    }
                }
            };

            fetchVeronline();
            return;
        }

        // 5. External Embed servers (player4u, videasy, moviesapi, iframe)
        setIsStreaming(false);
        const embedUrl = getEmbedUrl(selectedServer, type, tmdbId, season, episode, item);
        setVideoUrl(null);
        setIframeSrc(embedUrl);
        setStreams([]);
        setLoading(false);

        return () => {
            isMounted = false;
        };
    }, [selectedServer, item, file, type, tmdbId, season, episode, initialRuntimeMins]);

    const selectStream = (st: StreamOption) => {
        setSelectedStream(st);
        if (selectedServer === "AIOStream") {
            const streamUrl = st.raw.url;
            const vUrl = TorresstreamApi.get("streamRemoteFile").getUrl({fileUrl: streamUrl});
            setVideoUrl(vUrl);
            setOriginalVideoUrl(streamUrl);
            setLoading(false);
            TorresstreamApi.getFileUrlMetadata(streamUrl)
                .then((metaRes: any) => {
                    const dur = getDurationFromMetadata(metaRes.metadata, item);
                    if (dur) setDuration(dur);
                    setAudios(getAudioFromMetadata(metaRes.metadata));
                    setSubtitles(getSubtitlesFromMetadata(metaRes.metadata));
                })
                .catch(() => {});
        } else if (selectedServer === "TowersStream") {
            const torId = st.raw.id;
            TorresstreamApi.downloadTorrent(torId, tmdbId, season, episode).catch(() => {});
            const vUrl = getEmbedUrl("TowersStream", undefined, torId, undefined, undefined);
            setVideoUrl(vUrl);
            setOriginalVideoUrl(TorresstreamApi.get("originalFile").getUrl({torrentId: torId}));
            setLoading(false);
            TorresstreamApi.getTorrentFileDetails(torId)
                .then((metaRes: any) => {
                    const dur = getDurationFromMetadata(metaRes.metadata, item);
                    if (dur) setDuration(dur);
                    setAudios(getAudioFromMetadata(metaRes.metadata));
                    setSubtitles(getSubtitlesFromMetadata(metaRes.metadata));
                })
                .catch(() => {});
        } else if (selectedServer === "VerOnline") {
            if (st.raw.customFunction) {
                setIsStreaming(true);
                setIframeSrc(null);
                const url = TorresstreamApi.get(st.raw.customFunction).getUrl({
                    link: st.raw.link,
                    tmdbId,
                    season,
                    episode
                });
                setVideoUrl(url);
                setOriginalVideoUrl(url);
            } else {
                setIsStreaming(false);
                setVideoUrl(null);
                setIframeSrc(st.raw.link);
            }
            setLoading(false);
        }
    };

    const finalDurationMins = duration || initialRuntimeMins;
    const finalDurationSecs = Math.round(finalDurationMins * 60);

    return {
        loading,
        error,
        videoUrl,
        originalVideoUrl,
        iframeSrc,
        duration: finalDurationSecs,
        durationMinutes: finalDurationMins,
        subtitles,
        audios,
        streams,
        selectedStream,
        selectStream,
        downloadInfo,
        isStreaming
    };
};

export default useTvStreamResolver;
